import {Children, cloneElement, ReactElement, forwardRef, ReactNode, RefObject, useContext, useEffect, useRef, useState} from 'react';
import BlockHoverButtons from "./BlockHoverButtons";
import classNames from "classnames";
import {StoreContext} from "../../../stores/StoreLoader";
import SearchResultFooter from "./_SearchResultFooter";
import {observer} from "mobx-react";
import ExpandButton from "./ExpandButton";
import {gridTypesEnum} from "../../../utils/SchoolBlocksUtilities";

import {itemResize, itemStopResize} from "./blockUtils";
import "jquery";
import {IView} from "../../../stores/InterfaceStore";

let Draggabilly;

interface BlockWrapperProps {
    blockExpanded: boolean,
    blockObj: AnyBlockObj,
    blockRef: RefObject<any>,
    children?: ReactNode,
    className?: string,
    gridType: string,
    htmlId: string,
    isFirst: boolean,
    locksAreEnabled: boolean,
    maxColumns: number,
    normalizedFirstCharacter: string,
    outsideGrid: boolean,
    packeryInstance?: any, //TODO: need to change this to specific when there is time.
    packeryLayout?: () => void,
    sizeX: number,
    sizeY: number,
    showExpandButton: boolean,
    setBlockExpanded: (value: boolean) => void,
    hasMicrosite: boolean,
    isSearchResult: boolean,
}

const BlockWrapper = observer(forwardRef((props: BlockWrapperProps, ref: RefObject<any>) => {
    const {packeryInstance, packeryLayout, locksAreEnabled, blockObj, blockRef, gridType, outsideGrid, isFirst, normalizedFirstCharacter, blockExpanded} = props;
    const [position, setPosition] = useState<{left?: number, top?: number}>({left: undefined, top: undefined});
    const {gridStore, i18nStore, modalStore, userStore, interfaceStore} = useContext(StoreContext);

    const blockStyle = {
        "overflow": gridType === gridTypesEnum.ALPHA_GRID ? "visible" : "hidden",
        ...position,
    };

    let columnWidth = 0,
        rowHeight = 0;

    if (packeryInstance) {
        columnWidth = Math.round(packeryInstance.columnWidth);
        rowHeight = Math.round(packeryInstance.rowHeight);
    }

    let {posX, posY} = blockObj;
    if (posX === null || posX < 0) {
        posX = 0;
    }
    if (posY === null || posY < 0) {
        posY = 0;
    }

    const isModalTrigger = interfaceStore.view === IView.MAP;
    const gridWidth = Math.ceil(props.maxColumns * columnWidth);
    const blockWidth = Math.ceil(props.sizeX * columnWidth);
    const pixelsX = posX * columnWidth,
          pixelsY = posY * rowHeight;

    const shouldBeLockedBasedOnGrid = gridType === gridTypesEnum.FLEX_GRID
        && locksAreEnabled
        && blockObj.positionLocked
        && gridWidth >= 1216
        && gridWidth >= pixelsX + blockWidth;

    // This effect ensures that the block is rendered and then we come back and determine where the
    // locked block should be placed.  If something changes like the grid width, lock state, size,
    // etc, then the effect is run again and the position state is changed.  Pretty slick.
    useEffect(() => {
        if (shouldBeLockedBasedOnGrid) {
            setPosition({left: pixelsX, top: pixelsY});
        } else {
            setPosition({left: undefined, top: undefined});
        }
    }, [shouldBeLockedBasedOnGrid, pixelsX, pixelsY]);

    // When the position changes, make sure we stamp/unstamp the block and call packeryLayout()
    useEffect(() => {
        if (packeryInstance && packeryLayout) {
            if (gridType === gridTypesEnum.FLEX_GRID) {
                // unstamp everything first.  the stamp function doesn't track what's already been stamped, so it could
                // be dangerous to stamp without unstamping first.
                packeryInstance.unstamp(blockRef.current)
                if (shouldBeLockedBasedOnGrid) {
                    packeryInstance.stamp(blockRef.current)
                }
            }

            // layout should be called regardless of the gridType because something has changed (position, size, etc).
            packeryLayout();
        }
    }, [packeryInstance, shouldBeLockedBasedOnGrid, position.left, position.top, props.sizeX, props.sizeY]);

    const widthCssClass = 'sb-w' + props.sizeX;

    const wrapperClassNames = classNames(
        'sb-block',
        'sb-type-' + blockObj.blockType,
        widthCssClass,
        'sb-min-height',
        props.className,
        {
            [`sb-h${props.sizeY}`]: !props.isSearchResult,
            [`sb-search-h${props.sizeY}`]: props.isSearchResult,
            'sb-position-locked': shouldBeLockedBasedOnGrid || blockObj.fluxLocked, // added separate flux key so that we can influence this class programmatically without altering the positionIsLocked data attribute
            'hidden': !!(gridStore && !gridStore.blockVisibility.get(blockObj.id)?.isVisible && !outsideGrid),
            'sb-flux-capacitor-resized': blockObj.fluxResized,
            'sb-flux-capacitor-moved': blockObj.fluxMoved,
            'sb-flux-capacitor-deleted': blockObj.fluxDeleted,
            'sb-flux-capacitor-parent-deleted': blockObj.fluxParentDeleted,
            'sb-flux-capacitor-locked': blockObj.fluxLocked,
            'sb-flux-capacitor-unlocked': blockObj.fluxUnlocked,
        },
    );

    const wrapperHtmlAttributes = {
        'id': props.htmlId,
        'role': isModalTrigger ? 'button' : 'article',
        'aria-label': isModalTrigger ? 'Click to open ' + blockObj.filterString + ' modal'
            : blockObj.filterString,
        'data-id': blockObj.id,
        'data-blocktype': blockObj.blockType,
        'data-load-async': blockObj.loadAsync,
        'data-size-x': blockObj.sizeX,
        'data-size-y': blockObj.sizeY,
        'data-pos-x': blockObj.posX,
        'data-pos-y': blockObj.posY,
        'data-filter-string': blockObj.filterString,
        'data-filter-category': blockObj.filterCategories,
        'data-name': blockObj.name,
    };

    const blockContainerClassNames = classNames({
        'sb-block-container': true,
        'sb-withSearchFooter': props.isSearchResult,
    });

    let header;
    if (props.isSearchResult) {
        const footerData = blockObj.footerData;
        header = <SearchResultFooter blockObj={blockObj}
                                     url={footerData.url}
                                     parentText={footerData.parentText}
                                     parentType={footerData.parentType}
                                     grandParentText={footerData.grandParentText}
                                     cssWidthClass={widthCssClass}
                                     cssColorClass={footerData.cssColorClass}/>
    }


    // whenever a block is expanded we need to trigger a layout
    useEffect(() => {
        if (blockExpanded && blockRef.current) {
            blockRef.current.style.height = (Math.ceil(blockRef.current.scrollHeight / rowHeight) * rowHeight) - 14 + "px";
            if (packeryLayout) {
                packeryLayout();
            }
        }
    }, [blockExpanded]);

    function handleClick() {
        if (blockObj.blockType) {
            modalStore.addModal({
                type: blockObj.blockType === "organization" ? "sbOrganization" : "schoolDistrict",
                orgId: blockObj.id,
                id: blockObj.id,
                blockObj,
            });
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            handleClick();
        }
    }

    useEffect(() => {
        require("jquery-ui/ui/core");
        require("jquery-ui/ui/widget");
        require("jquery-ui/ui/mouse");
        require("jquery-ui/ui/resizable");
        import("jquery-ui-touch-punch/jquery.ui.touch-punch")
        Draggabilly = require('draggabilly');
    }, []);

    const [blockHovered, setBlockHovered] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const hasIframe = useRef(false);
    const draggabillified = useRef(false);

    const canShowHoverButtons = gridType === gridTypesEnum.FLEX_GRID &&
        userStore.editor &&
        gridStore.canEdit &&
        !props.outsideGrid;

    function handleMouseEnter() {
        setBlockHovered(true);

        if (
            canShowHoverButtons &&
            !blockHovered &&
            Draggabilly &&
            blockObj.blockType !== "content_item"
        ) {
            const $itemElem = $(ref.current) as any;

            // make element draggable with Draggabilly
            const draggie = new Draggabilly(ref.current, {
                'handle': '.sb-move-icon',
            });

            // Prevent re-biding Draggabilly events
            if (packeryInstance && draggabillified.current !== true) {
                // bind Draggabilly events to Packery
                packeryInstance.bindSchoolBlocksDraggabillyEvents(draggie);
                draggabillified.current = true;
                hasIframe.current = !!ref.current.querySelector("iframe");

                // only do this once
                $itemElem.on('resizestart', function () {
                    setIsResizing(true);
                });
                $itemElem.on('resize', function(event) {
                    itemResize(packeryInstance, event);
                });
                $itemElem.on('resizestop', function (event, ui) {
                    itemStopResize(i18nStore.currentOrgSupportedLocale, gridStore, packeryInstance, event, ui);
                    setIsResizing(false);
                });
            }

            if (shouldBeLockedBasedOnGrid) {
                draggie.disable(); // disable dragging if locked
            } else {
                $itemElem.resizable({'handles': 'e, s, se'}); // handle resizing
            }
        }

    }

    function handleMouseLeave() {
        setBlockHovered(false);

        if (
            canShowHoverButtons &&
            !isResizing &&
            Draggabilly && ref
        ) {

            const draggie = new Draggabilly(ref.current, {
                'handle': '.sb-move-icon',
            });
            draggie.disable();

            const $itemElem = $(ref.current) as any;

            $itemElem.removeClass('draggable ui-draggable ui-draggable-handle');

            if ($itemElem.resizable('instance')) {
                $itemElem.resizable('destroy');
            } else {
                $itemElem.removeClass('resizable ui-resizable');
                $itemElem.find('.ui-resizable-handle').remove();
            }
        }

    }

    return <article
            ref={ref}
            onClick={isModalTrigger ? handleClick : () => {
            }}
            onKeyDown={isModalTrigger ? handleKeyDown : () => {
            }}
            className={wrapperClassNames}
            {...wrapperHtmlAttributes}
            tabIndex={isModalTrigger ? 0 : undefined}
            style={blockStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
        {gridType === gridTypesEnum.ALPHA_GRID && <div className={"sb-multigrid-block-header"}>
            {isFirst && <span aria-label={`Blocks beginning with ${normalizedFirstCharacter === "#" ? "a number" : `the letter ${normalizedFirstCharacter}`}`}>
                {normalizedFirstCharacter}
            </span>}
        </div>}
        <BlockHoverButtons
            blockRef={blockRef}
            blockObj={blockObj}
            hidden={!canShowHoverButtons || !blockHovered || blockObj.blockModel.deleted}
            packeryInstance={packeryInstance}
            hasMicrosite={props.hasMicrosite}
        />
        {header}
        <div className={blockContainerClassNames}>
            {isResizing && hasIframe.current && <div className="ui-resizable-iframeFix" style={{
                    width:'100%',
                    height: '100%',
                    position: "absolute",
                    opacity: "0.001",
                    zIndex: 10000,
            }} />}
            {Children.map(props.children, child => cloneElement((child as ReactElement), {
                blockHovered: blockHovered,
            }))}
            {props.showExpandButton && <ExpandButton
                    setBlockExpanded={props.setBlockExpanded}
                    blockExpanded={props.blockExpanded}
                />}
        </div>
    </article>
}));

export default BlockWrapper;
